import { Package, Search, Clock, CheckCircle, AlertCircle, Loader2, Phone, Image, Plus } from "lucide-react"
import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Layout } from "@/components/Layout"
import RegistrarEncomendaModal from "@/components/RegistrarEncomendaModal"
import LiberarEncomendaModal from "@/components/LiberarEncomendaModal"
import { usePackages, useResidentPackages } from "@/hooks/usePackages"
import { useAuth } from "@/hooks/useAuth"
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog"

const getStatusIcon = (status: string) => {
  switch (status) {
    case "delivered":
      return <CheckCircle className="h-4 w-4 text-green-500" />
    case "pending":
      return <Clock className="h-4 w-4 text-yellow-500" />
    default:
      return <AlertCircle className="h-4 w-4 text-red-500" />
  }
}

const getStatusVariant = (status: string) => {
  switch (status) {
    case "delivered":
      return "default" as const
    case "pending":
      return "secondary" as const
    default:
      return "destructive" as const
  }
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case "delivered":
      return "Entregue"
    case "pending":
      return "Aguardando"
    default:
      return "Desconhecido"
  }
}

const Encomendas = () => {
  const { profile } = useAuth()
  const [searchTerm, setSearchTerm] = useState("")
  
  // Moradores veem apenas encomendas da sua unidade
  // Staff (porteiro, sindico, administracao) veem todas as encomendas do condomínio
  const isResident = profile?.role === 'morador'
  const { data: allPackages, isLoading: allLoading, error: allError } = usePackages()
  const { data: residentPackages, isLoading: residentLoading, error: residentError } = useResidentPackages()
  
  const encomendas = isResident ? residentPackages : allPackages
  const isLoading = isResident ? residentLoading : allLoading
  const error = isResident ? residentError : allError

  const filteredEncomendas = encomendas?.filter(encomenda => {
    const searchLower = searchTerm.toLowerCase()
    const recipientName = encomenda.recipient_name || ""
    const sender = encomenda.sender || ""
    const description = encomenda.description || ""
    const unitNumber = encomenda.unit?.number || ""
    const uniqueCode = encomenda.unique_code || ""
    
    return recipientName.toLowerCase().includes(searchLower) ||
           sender.toLowerCase().includes(searchLower) ||
           description.toLowerCase().includes(searchLower) ||
           unitNumber.includes(searchTerm) ||
           uniqueCode.toLowerCase().includes(searchLower)
  }) || []

  // Ordenar encomendas: pendentes primeiro, depois entregues, por data
  const sortedEncomendas = filteredEncomendas.sort((a, b) => {
    // 1º Critério: Status (pending sempre antes de delivered)
    if (a.status !== b.status) {
      if (a.status === 'pending') return -1;
      if (b.status === 'pending') return 1;
    }
    
    // 2º Critério: Data (dentro do mesmo status)
    if (a.status === 'pending') {
      // Para pendentes: ordenar por data de recebimento (mais recente primeiro)
      const dateA = new Date(a.received_at || a.created_at || 0).getTime();
      const dateB = new Date(b.received_at || b.created_at || 0).getTime();
      return dateB - dateA;
    } else {
      // Para entregues: ordenar por data de entrega (mais recente primeiro)
      const dateA = new Date(a.delivered_at || 0).getTime();
      const dateB = new Date(b.delivered_at || 0).getTime();
      return dateB - dateA;
    }
  })

  // Calcular estatísticas (usar 'encomendas' original, não filteredEncomendas)
  const totalAguardando = encomendas?.filter(e => e.status === 'pending').length || 0
  
  const encomendasEntregues = encomendas?.filter(e => e.status === 'delivered') || []
  const totalEntregues = encomendasEntregues.length
  
  // Entregues hoje
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  const entreguesHoje = encomendasEntregues.filter(e => {
    if (!e.delivered_at) return false
    const dataEntrega = new Date(e.delivered_at)
    dataEntrega.setHours(0, 0, 0, 0)
    return dataEntrega.getTime() === hoje.getTime()
  }).length
  
  // Entregues essa semana (últimos 7 dias)
  const seteDiasAtras = new Date()
  seteDiasAtras.setDate(seteDiasAtras.getDate() - 7)
  seteDiasAtras.setHours(0, 0, 0, 0)
  const entreguesSemana = encomendasEntregues.filter(e => {
    if (!e.delivered_at) return false
    const dataEntrega = new Date(e.delivered_at)
    return dataEntrega >= seteDiasAtras
  }).length

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center text-red-600">
            Erro ao carregar encomendas: {error.message}
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ita-black">Encomendas</h1>
            <p className="text-gray-600">
              {isResident ? "Visualize suas encomendas" : "Gerencie as encomendas do condomínio"}
            </p>
          </div>
          {!isResident && (
            <RegistrarEncomendaModal>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Registrar Encomenda
              </Button>
            </RegistrarEncomendaModal>
          )}
        </div>

        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card Aguardando */}
            <Card className="border-l-4 border-l-yellow-500">
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Clock className="h-4 w-4 text-yellow-500" />
                    Aguardando Retirada
                  </CardTitle>
                  <Badge variant="secondary" className="text-base px-2 py-0.5">
                    {totalAguardando}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0">
                <p className="text-xs text-muted-foreground">
                  {totalAguardando === 0 
                    ? "Nenhuma encomenda aguardando" 
                    : totalAguardando === 1 
                    ? "1 encomenda aguardando retirada"
                    : `${totalAguardando} encomendas aguardando retirada`}
                </p>
              </CardContent>
            </Card>

          {/* Card Entregues */}
            <Card className="border-l-4 border-l-green-500">
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Entregues
                  </CardTitle>
                  <Badge variant="default" className="text-base px-2 py-0.5">
                    {totalEntregues}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0">
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Hoje:</span>
                    <span className="font-semibold">{entreguesHoje}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Essa semana:</span>
                    <span className="font-semibold">{entreguesSemana}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs pt-1 border-t">
                    <span className="text-muted-foreground">Total:</span>
                    <span className="font-semibold">{totalEntregues}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
        </div>

        <div className="flex items-center space-x-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input 
              placeholder="Buscar por nome, código, unidade..." 
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {sortedEncomendas.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {searchTerm ? "Nenhuma encomenda encontrada" : "Nenhuma encomenda cadastrada"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedEncomendas.map((encomenda) => (
          <Card 
            key={encomenda.id} 
            className={`ita-card transition-all ${
              searchTerm.trim() !== '' 
                ? 'bg-blue-50 dark:bg-blue-950/20 border-2 border-blue-300 dark:border-blue-700 shadow-lg' 
                : ''
            }`}
          >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Package className="h-5 w-5 text-ita-blue" />
                      <CardTitle className="text-lg font-semibold">
                        {encomenda.unique_code || `#${encomenda.id.slice(-8)}`}
                      </CardTitle>
                    </div>
                    <Badge variant={getStatusVariant(encomenda.status || "pending")}>
                      <div className="flex items-center space-x-1">
                        {getStatusIcon(encomenda.status || "pending")}
                        <span>{getStatusLabel(encomenda.status || "pending")}</span>
                      </div>
                    </Badge>
                  </div>
                  <CardDescription>
                    Unidade {encomenda.unit?.number || "N/A"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="font-medium text-gray-900">{encomenda.recipient_name}</p>
                    {encomenda.sender && (
                      <p className="text-sm text-gray-600">Empresa: {encomenda.sender}</p>
                    )}
                    {encomenda.description && (
                      <p className="text-sm text-gray-600">Pedido: {encomenda.description}</p>
                    )}
                    {encomenda.whatsapp_phone && (
                      <div className="flex items-center space-x-1 text-sm text-gray-600">
                        <Phone className="h-3 w-3" />
                        <span>{encomenda.whatsapp_phone}</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      Recebido: {new Date(encomenda.received_at || "").toLocaleDateString('pt-BR')}
                    </p>
                    {encomenda.status === "delivered" && (
                      <>
                        {encomenda.notes && (
                          <p className="text-xs text-gray-600 font-medium">
                            Retirado por: {encomenda.notes}
                          </p>
                        )}
                        {encomenda.delivered_at && (
                          <p className="text-xs text-gray-500">
                            Entregue: {new Date(encomenda.delivered_at).toLocaleDateString('pt-BR')} às {new Date(encomenda.delivered_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    {encomenda.photo_url && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Image className="h-4 w-4 mr-1" />
                            Ver Foto
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <img 
                            src={encomenda.photo_url} 
                            alt="Foto da encomenda"
                            className="w-full h-auto rounded"
                          />
                        </DialogContent>
                      </Dialog>
                    )}

                    {encomenda.status === "pending" && encomenda.unique_code && (
                      <LiberarEncomendaModal 
                        packageId={encomenda.id} 
                        uniqueCode={encomenda.unique_code}
                      >
                        <Button size="sm" className="ml-auto">
                          Liberar Encomenda
                        </Button>
                      </LiberarEncomendaModal>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}

export default Encomendas
